import { clsx } from '@alexis/helpers/clsx';
import CheckboxItem from '@pages/FlightSearchResult/components/CheckboxItem';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import { GenzoTrackApplyFilterActionEventType } from '@pages/FlightSearchResult/types';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  convertFilterStateToDataForTracking,
  getGenzoValuesForBookingOptionsFilter,
} from '@helpers/flight';

import useFlightSearchResultsDatadog from '@hooks/useFlightSearchResultsDatadog';

import { getTranslationsState } from '@redux/selectors';

import { FlightSearchResultSearchParams } from '@wegoTypes/flights/flightSearchParams';

import styles from '../FlightFilterCategoryContent.module.scss';

type BookingOptionsFilterProps = {
  className?: string;
  genzoTrackApplyFilterSearchView: () => void;
  genzoTrackApplyFilterActionEvent: GenzoTrackApplyFilterActionEventType;
};

const BookingOptionsFilter: React.FC<BookingOptionsFilterProps> = ({
  className,
  genzoTrackApplyFilterSearchView,
  genzoTrackApplyFilterActionEvent,
}) => {
  const { providerFilterOptions } = useFlightSearchResults();
  const { isPerLegSearch } = useFlightPLS();

  const translations = useSelector(getTranslationsState);

  const { activeLeg, providersFilter, toggleMultiCitySingleFilter } = useFlightSearchFilters();

  const selectedProvidersForActiveLeg = useMemo(
    () => providersFilter.find((item) => item.leg === activeLeg)?.values || [],
    [providersFilter, activeLeg],
  );
  const { recordChangeFilterAction } = useFlightSearchResultsDatadog();

  const handleProvidersChanged = (provider: string) => {
    recordChangeFilterAction();

    const newFilter = toggleMultiCitySingleFilter({
      activeLeg,
      searchParamKey: FlightSearchResultSearchParams.Providers,
      value: provider,
    });

    const newFilterWithValuesToTrack = newFilter.map((filter) => {
      filter.values = getGenzoValuesForBookingOptionsFilter(filter.values);
      return filter;
    });

    genzoTrackApplyFilterActionEvent('filter', 'applied', {
      booking_options: convertFilterStateToDataForTracking(
        newFilterWithValuesToTrack,
        false,
        true,
        isPerLegSearch,
        activeLeg,
      ),
    });
    genzoTrackApplyFilterSearchView();
  };

  const hasWegoFareProvider = providerFilterOptions.some((provider) => provider.wegoFare);

  const hasDirectAirlineProvider = providerFilterOptions.some(
    (provider) => provider.type === 'airline',
  );

  if (!hasWegoFareProvider && !hasDirectAirlineProvider) return null;

  return (
    <div className={clsx(styles.categoryContent, !!className && styles.inDropdown)}>
      {hasWegoFareProvider && (
        <CheckboxItem
          className={className}
          isChecked={selectedProvidersForActiveLeg.includes('wego')}
          label={translations.book_on_wego as string}
          onCheckboxToggle={() => handleProvidersChanged('wego')}
        />
      )}

      {hasDirectAirlineProvider && (
        <CheckboxItem
          className={className}
          isChecked={selectedProvidersForActiveLeg.includes('airline')}
          label={translations.lbl_book_direct_on_airline as string}
          onCheckboxToggle={() => handleProvidersChanged('airline')}
        />
      )}
    </div>
  );
};

export default BookingOptionsFilter;
